import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "pg_cookie_consent_v1"; // values: "accepted" | "rejected"
const PLAUSIBLE_DOMAIN = "pipeguide.tech";
const PLAUSIBLE_SCRIPT = "https://plausible.io/js/script.js";

function loadPlausible() {
    if (window.__pgPlausibleLoaded) return;
    const s = document.createElement("script");
    s.defer = true;
    s.setAttribute("data-domain", PLAUSIBLE_DOMAIN);
    s.src = PLAUSIBLE_SCRIPT;
    document.head.appendChild(s);
    window.__pgPlausibleLoaded = true;
}

export default function CookieBanner() {
    const [open, setOpen] = useState(false);

    useEffect(() => {
        try {
            const choice = localStorage.getItem(STORAGE_KEY);
            if (choice === "accepted") {
                loadPlausible();
            } else if (!choice) {
                // Slight delay so it doesn't pop on first paint
                const t = setTimeout(() => setOpen(true), 800);
                return () => clearTimeout(t);
            }
        } catch (e) {
            // localStorage unavailable: do nothing (don't track, don't block)
        }
    }, []);

    const accept = () => {
        try { localStorage.setItem(STORAGE_KEY, "accepted"); } catch (e) {}
        loadPlausible();
        setOpen(false);
    };

    const reject = () => {
        try { localStorage.setItem(STORAGE_KEY, "rejected"); } catch (e) {}
        setOpen(false);
    };

    if (!open) return null;

    return (
        <div
            data-testid="cookie-banner"
            className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 z-50 max-w-md rounded-2xl border border-zinc-200 bg-white shadow-2xl p-5 fade-up"
            role="dialog"
            aria-labelledby="cookie-title"
        >
            <div className="flex items-start gap-3">
                <div className="h-9 w-9 shrink-0 rounded-lg bg-brand-tint flex items-center justify-center">
                    <Cookie className="h-4 w-4 text-brand-primary" />
                </div>
                <div className="flex-1 min-w-0">
                    <p id="cookie-title" className="font-display text-base font-semibold text-zinc-900">
                        Cookies & analytics
                    </p>
                    <p className="mt-1.5 text-sm text-zinc-600 leading-relaxed">
                        We use a privacy-friendly analytics tool (Plausible — no personal data, no cross-site tracking) to understand which pages help. You can opt in or out, change your mind any time.
                        See our <Link to="/privacy" className="text-brand-primary hover:underline">Privacy Policy</Link>.
                    </p>
                    <div className="mt-4 flex flex-wrap gap-2">
                        <button
                            data-testid="cookie-accept"
                            onClick={accept}
                            className="h-9 px-4 rounded-lg bg-brand-primary hover:bg-brand-hover text-white text-sm font-medium"
                        >
                            Accept analytics
                        </button>
                        <button
                            data-testid="cookie-reject"
                            onClick={reject}
                            className="h-9 px-4 rounded-lg bg-white border border-zinc-300 hover:bg-zinc-50 text-zinc-900 text-sm font-medium"
                        >
                            Decline
                        </button>
                    </div>
                </div>
                <button
                    onClick={reject}
                    aria-label="Dismiss"
                    data-testid="cookie-close"
                    className="text-zinc-400 hover:text-zinc-700 p-1"
                >
                    <X className="h-4 w-4" />
                </button>
            </div>
        </div>
    );
}
